import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Loader2, Building2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { DepartmentPerformanceTable } from "@/components/intelligence/DepartmentPerformanceTable";
import { DepartmentDetailPanel } from "@/components/intelligence/DepartmentDetailPanel";
import { Card, CardContent } from "@/components/ui/card";
import { formatInr } from "@/lib/intelligenceFormat";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function DepartmentIntelligencePage() {
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");
	const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-departments"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return superHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (isChainScoped && chainHospitals.length) {
			return chainHospitals.map((h) => ({ id: h.id, name: h.name }));
		}
		if (user?.hospitalId) {
			const name = chainHospitals.find((h) => h.id === user.hospitalId)?.name ?? "My Hospital";
			return [{ id: user.hospitalId, name }];
		}
		return [];
	}, [isSuperAdmin, isChainScoped, superHospitals, chainHospitals, user?.hospitalId]);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const queryParams = {
		period: period.preset as IntelligencePeriodPreset,
		from: period.preset === "custom" ? period.from : undefined,
		to: period.preset === "custom" ? period.to : undefined,
		hospitalId: reportHospitalId,
		compare: true,
	};

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["intelligence-departments", queryParams],
		queryFn: () => intelligenceApi.getDepartments(queryParams),
		enabled: customReady,
	});

	const { data: detail, isLoading: detailLoading } = useQuery({
		queryKey: ["intelligence-department-detail", selectedDepartmentId, queryParams],
		queryFn: () => intelligenceApi.getDepartmentDetail(selectedDepartmentId!, queryParams),
		enabled: customReady && !!selectedDepartmentId,
	});

	return (
		<div className="space-y-6 pb-10">
			<IntelligenceSubNav basePath={isSuperAdmin ? "/super-admin" : "/hospital-admin"} />

			<div>
				<div className="flex items-center gap-2 text-indigo-700">
					<Building2 className="h-6 w-6" />
					<h1 className="text-2xl font-bold tracking-tight text-slate-900">
						Department Intelligence
					</h1>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					Department-level revenue and volume · {data?.period.label ?? "—"}
				</p>
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end">
					<IntelligenceServerExportButton
						report="departments"
						params={queryParams}
						filename={`department-intelligence-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
					/>
				</div>
			)}

			{isLoading && customReady && (
				<div className="flex justify-center py-16 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" /> Loading…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/40">
					<CardContent className="pt-4 text-sm text-destructive flex gap-2">
						<AlertTriangle className="h-4 w-4 shrink-0" />
						{(error as Error)?.message ?? "Failed to load department intelligence"}
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<>
					<div className="flex flex-wrap gap-4 text-sm">
						<span className="text-muted-foreground">
							Total attributed revenue:{" "}
							<strong className="text-foreground tabular-nums">{formatInr(data.totalRevenue)}</strong>
						</span>
						<span className="text-muted-foreground">
							{data.departments.length} departments with activity
						</span>
					</div>

					{data.limitations[0] && (
						<p className="text-xs text-muted-foreground">{data.limitations[0]}</p>
					)}

					<div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
						<DepartmentPerformanceTable
							departments={data.departments}
							selectedDepartmentId={selectedDepartmentId}
							onSelectDepartment={setSelectedDepartmentId}
						/>
						<div>
							{selectedDepartmentId && detailLoading && (
								<div className="flex justify-center py-12">
									<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
								</div>
							)}
							{selectedDepartmentId && detail && <DepartmentDetailPanel detail={detail} />}
							{!selectedDepartmentId && (
								<Card className="h-full min-h-[200px] flex items-center justify-center">
									<CardContent className="text-sm text-muted-foreground text-center py-12">
										Select a department to view detail, trends, and doctor contributions
									</CardContent>
								</Card>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
}
